import { branding } from '@guridosdrones/shared';
import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
}

const BASE_URL = `https://${branding.primaryDomain}`;
const SITE_NAME = branding.brandName;

const DEFAULT_DESCRIPTION =
  'Formação completa em drones de pulverização agrícola. Curso de pilotagem, mapeamento aéreo 3D e manutenção. Do zero à atuação com segurança no agro.';

const DEFAULT_KEYWORDS =
  'curso drone agrícola, piloto drone pulverização, mapeamento aéreo 3D, guri dos drones, drone dji agras, drone xag, agricultura de precisão, curso piloto agrícola, pulverização aérea';

function updateMeta(attr: string, key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function updateLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  image,
  url,
  type = 'website',
}: SEOProps) {
  const pageTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} | Curso de Piloto de Drone Agrícola`;
  const pageUrl = url || BASE_URL;
  const pageImage = image || `${BASE_URL}/og-image.webp`;

  useEffect(() => {
    document.title = pageTitle;

    // Standard meta
    updateMeta('name', 'title', pageTitle);
    updateMeta('name', 'description', description);
    updateMeta('name', 'keywords', keywords);

    // Open Graph
    updateMeta('property', 'og:type', type);
    updateMeta('property', 'og:url', pageUrl);
    updateMeta('property', 'og:title', pageTitle);
    updateMeta('property', 'og:description', description);
    updateMeta('property', 'og:image', pageImage);
    updateMeta('property', 'og:image:alt', `${SITE_NAME} - Curso de Piloto de Drone Agrícola`);
    updateMeta('property', 'og:site_name', SITE_NAME);
    updateMeta('property', 'og:locale', 'pt_BR');

    // Twitter (uses name, not property, per spec)
    updateMeta('name', 'twitter:card', 'summary_large_image');
    updateMeta('name', 'twitter:url', pageUrl);
    updateMeta('name', 'twitter:title', pageTitle);
    updateMeta('name', 'twitter:description', description);
    updateMeta('name', 'twitter:image', pageImage);
    updateMeta('name', 'twitter:image:alt', `${SITE_NAME} - Curso de Piloto de Drone Agrícola`);

    // Canonical
    updateLink('canonical', pageUrl);

    // JSON-LD Structured Data
    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${BASE_URL}/#website`,
          url: BASE_URL,
          name: SITE_NAME,
          description,
          publisher: { '@id': `${BASE_URL}/#organization` },
          inLanguage: 'pt-BR',
        },
        {
          '@type': 'Organization',
          '@id': `${BASE_URL}/#organization`,
          name: SITE_NAME,
          url: BASE_URL,
          logo: {
            '@type': 'ImageObject',
            url: `${BASE_URL}/favicon/favicon-96x96.png`,
            width: 96,
            height: 96,
          },
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: branding.contact.phone,
            contactType: 'customer service',
            availableLanguage: 'Portuguese',
            areaServed: 'BR',
          },
          address: {
            '@type': 'PostalAddress',
            streetAddress: branding.contact.address.line1,
            addressLocality: branding.contact.address.city,
            addressRegion: branding.contact.address.state,
            postalCode: branding.contact.address.zip,
            addressCountry: branding.contact.address.country,
          },
          sameAs: [branding.social.instagram, branding.social.youtube, branding.social.tiktok],
        },
        {
          '@type': 'Course',
          '@id': `${BASE_URL}/#course`,
          name: 'Curso de Piloto de Drone Agrícola de Pulverização',
          description:
            'Formação completa em drones de pulverização agrícola: pilotagem de drones agrícolas, mapeamento aéreo 3D com acompanhamento de relevo e manutenção estratégica.',
          provider: { '@id': `${BASE_URL}/#organization` },
          url: pageUrl,
          inLanguage: 'pt-BR',
          hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: 'Online',
            courseWorkload: 'PT40H',
            instructor: {
              '@type': 'Person',
              name: 'Eduardo dos Anjos',
              alternateName: 'Guri dos Drones',
              jobTitle: 'Instrutor de Drones Agrícolas',
            },
          },
          offers: {
            '@type': 'Offer',
            category: 'Curso Online',
            availability: 'https://schema.org/InStock',
            priceCurrency: 'BRL',
          },
          about: [
            { '@type': 'Thing', name: 'Pulverização Agrícola com Drones' },
            { '@type': 'Thing', name: 'Mapeamento Aéreo 3D' },
            { '@type': 'Thing', name: 'Manutenção de Drones Agrícolas' },
          ],
        },
        {
          '@type': 'WebPage',
          '@id': `${pageUrl}/#webpage`,
          url: pageUrl,
          name: pageTitle,
          description,
          isPartOf: { '@id': `${BASE_URL}/#website` },
          about: { '@id': `${BASE_URL}/#course` },
          inLanguage: 'pt-BR',
        },
        {
          '@type': 'FAQPage',
          '@id': `${BASE_URL}/#faq`,
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Para quem é o curso Guri dos Drones?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'O curso é para qualquer pessoa que deseja adquirir capacitação como piloto de drone agrícola, desde iniciantes até quem já tem experiência e quer se especializar em mapeamento 3D e manutenção.',
              },
            },
            {
              '@type': 'Question',
              name: 'Quais modelos de drones podem ser usados?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'O curso cobre drones DJI (T10, T25, Agras) e XAG (P100 Pro), que são os mais utilizados na pulverização agrícola no Brasil.',
              },
            },
            {
              '@type': 'Question',
              name: 'Como funciona a garantia do curso?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Você tem 7 dias de garantia incondicional. Se não ficar satisfeito, basta enviar um e-mail e devolvemos 100% do valor, sem perguntas.',
              },
            },
          ],
        },
      ],
    };

    let script = document.getElementById('json-ld-seo') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = 'json-ld-seo';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);
  }, [pageTitle, description, keywords, pageImage, pageUrl, type]);

  return null;
}
